import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActionCompletion, Actions, ofActionCompleted, Select, Store} from '@ngxs/store';
import {Observable, Subject} from 'rxjs';
import {OnboardingState} from '../../states/onboarding.state';
import {FetchOnboardingGoals} from '../../states/onboarding.actions';
import {takeUntil} from 'rxjs/operators';
import ResponseHandlingHelper from '../../helpers/response-handling-helper';
import {OnboardingGoalModelResponse} from '../../api/responses/onboarding-response'; // notice changed import
import {ActivatedRoute, Params, Router} from '@angular/router';
import {PushMenuActiveAccountId} from 'src/app/states/menu.actions';
import {HttpErrorCode} from 'src/app/api/http-error-code.enum';
import {Title} from '@angular/platform-browser';


@Component({
    selector: 'app-onboarding-page',
    templateUrl: './onboarding-page.component.html',
    styleUrls: ['./onboarding-page.component.scss']
})
export class OnboardingPageComponent implements OnInit, OnDestroy {

    constructor(
        private store: Store,
        private actions: Actions,
        private router: Router,
        private route: ActivatedRoute,
        private title: Title
    ) {
    }

    public accountId: number;

    @Select(OnboardingState.orders)
    public goalsListener: Observable<OnboardingGoalModelResponse[]>;
    public goals: OnboardingGoalModelResponse[] = [];

    public isLoading = false;

    private cancellationToken = new Subject();
    private gc = new Subject();

    public sortColumn: string = '';
    public sortDirection: 'asc' | 'desc' = 'asc';

    public ngOnInit() {
        this.title.setTitle('Onboarding - Dataedo Account');
        this.initEvents();
    }

    private fetchData() {
        this.cancellationToken.next();
        this.isLoading = true;
        this.store.dispatch(new FetchOnboardingGoals(this.accountId, this.cancellationToken));
    }

    private initEvents() {
        this.route.params
            .pipe(takeUntil(this.gc))
            .subscribe(params => this.onPathChange(params));

        this.goalsListener
            .pipe(takeUntil(this.gc))
            .subscribe(goals => this.goals = goals);

        this.actions
            .pipe(ofActionCompleted(FetchOnboardingGoals))
            .pipe(takeUntil(this.gc))
            .subscribe(event => this.onCompletedFetchGoals(event));
    }

    private onPathChange(params: Params) {
        this.accountId = parseInt(params.accountId, 10);
        this.store.dispatch(new PushMenuActiveAccountId(this.accountId));
        this.fetchData();
    }

    private async onCompletedFetchGoals(event: ActionCompletion<FetchOnboardingGoals, Error>) {
        const response = ResponseHandlingHelper.parse(event.result);
        if (response.isError && response.error.status === HttpErrorCode.Forbidden) {
            await this.router.navigate(['/accounts', this.accountId]);
        }

        this.isLoading = false;
    }

    public getStatusDotColor(status: string): string {
        switch (status.toUpperCase()) {
            case 'COMPLETED':
                return '#34c759'; // Green
            case 'IN_PROGRESS':
                return '#f4b400'; // Yellow
            case 'CONSULTING':
                return '#1e88e5'; // Blue
            case 'ACKNOWLEDGED':
                return '#9e9e9e'; // Grey
            default:
                return '#ccc';    // Default grey
        }
    }


    public getStatusLabel(status: string): string {
        switch (status.toUpperCase()) {
            case 'COMPLETED':
                return 'Done';
            case 'IN_PROGRESS':
                return 'In progress';
            case 'CONSULTING':
                return 'Consulting';
            case 'ACKNOWLEDGED':
                return 'Acknowledged';
            default:
                return status;
        }
    }

    public trackById(index: number, goal: OnboardingGoalModelResponse): number {
        return goal.id;
    }

    public ngOnDestroy(): void {
        this.cancellationToken.next();
        this.cancellationToken.complete();

        this.gc.next();
        this.gc.complete();
    }

    public sortGoals(column: string): void {
        if (!this.goals) { return; }

        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }
        const sorted = [...this.goals].sort((a: any, b: any) => {
            let aVal = a[column];
            let bVal = b[column];

            if (aVal == null) { return 1; }
            if (bVal == null) { return -1; }

            // Convert to timestamp if it's a date
            if (column.includes('date') || column.includes('updated_at') || column.includes('status_changed_at')) {
                aVal = new Date(aVal).getTime();
                bVal = new Date(bVal).getTime();
            }

            if (aVal < bVal) { return this.sortDirection === 'asc' ? -1 : 1; }
            if (aVal > bVal) { return this.sortDirection === 'asc' ? 1 : -1; }
            return 0;
        });

        this.goals = sorted;
    }


}
